import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { ShieldCheck, Lock, Zap, Loader2, Eye, EyeOff } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { errMsg } from "../lib/api";
import { BrandLogo } from "../components/Layout";

const TrustBullet = ({ icon: Icon, title, desc }) => (
  <div className="flex items-start gap-3">
    <div className="h-10 w-10 rounded-xl bg-white/15 flex items-center justify-center shrink-0">
      <Icon className="h-5 w-5 text-white" />
    </div>
    <div>
      <p className="font-semibold text-white text-sm">{title}</p>
      <p className="text-white/70 text-xs">{desc}</p>
    </div>
  </div>
);

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !password) return;
    setLoading(true);
    try {
      const user = await login(email, password);
      toast.success(`Selamat datang kembali, ${user.name.split(" ")[0]}!`);
      navigate(user.has_pin ? "/" : "/atur-pin");
    } catch (err) {
      toast.error(errMsg(err, "Email atau kata sandi salah"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-dvh grid lg:grid-cols-2 bg-background">
      {/* Brand panel */}
      <div className="hidden lg:flex flex-col justify-between bg-primary p-12 noise-overlay">
        <span className="font-logo font-bold text-3xl text-white">Quantum<span className="italic bg-gradient-to-r from-[#7EB6FF] to-[#2DD4BF] bg-clip-text text-transparent"> Pay</span></span>
        <div className="space-y-8 max-w-md">
          <h1 className="font-heading text-4xl font-semibold text-white leading-tight">
            Transfer antar bank, gratis dan aman.
          </h1>
          <div className="space-y-5">
            <TrustBullet icon={ShieldCheck} title="Keamanan berlapis" desc="Enkripsi data dan verifikasi di setiap transaksi" />
            <TrustBullet icon={Lock} title="PIN untuk setiap transaksi" desc="Semua pergerakan dana wajib dikonfirmasi dengan PIN 6 digit" />
            <TrustBullet icon={Zap} title="Cepat & tanpa biaya" desc="Transfer ke 10+ bank, QRIS, dan Virtual Account" />
          </div>
        </div>
        <p className="text-white/50 text-xs">© 2025 Quantum Pay — Mode Simulasi Sandbox</p>
      </div>

      {/* Form */}
      <div className="flex items-center justify-center p-6">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, ease: [0.2, 0.8, 0.2, 1] }}
          className="w-full max-w-md"
        >
          <div className="lg:hidden mb-8 text-center">
            <BrandLogo />
            <p className="text-xs text-muted-foreground mt-1">Dompet Digital & Transfer</p>
          </div>
          <Card className="rounded-2xl shadow-[0_10px_30px_rgba(11,18,32,0.08)]">
            <CardContent className="p-6 sm:p-8">
              <h2 className="font-heading text-2xl font-semibold mb-1">Masuk</h2>
              <p className="text-sm text-muted-foreground mb-6">Kelola keuanganmu dengan aman.</p>
              <form onSubmit={submit} className="space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="email">Email</Label>
                  <Input id="email" type="email" data-testid="login-email-input" placeholder="nama@email.com" value={email} onChange={(e) => setEmail(e.target.value)} className="h-11" required />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="password">Kata Sandi</Label>
                  <div className="relative">
                    <Input id="password" type={showPass ? "text" : "password"} data-testid="login-password-input" placeholder="••••••••" value={password} onChange={(e) => setPassword(e.target.value)} className="h-11 pr-10" required />
                    <button type="button" aria-label="Tampilkan kata sandi" onClick={() => setShowPass(!showPass)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                      {showPass ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
                <Button type="submit" className="w-full h-11 text-base" disabled={loading} data-testid="login-submit-button">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Masuk"}
                </Button>
              </form>
              <p className="text-sm text-center text-muted-foreground mt-6">
                Belum punya akun?{" "}
                <Link to="/daftar" className="text-primary font-medium hover:underline" data-testid="login-register-link">Daftar sekarang</Link>
              </p>
            </CardContent>
          </Card>
          <div className="mt-4 rounded-xl border bg-teal-50 border-teal-100 px-4 py-3 text-xs text-muted-foreground" data-testid="demo-credentials-hint">
            <span className="font-semibold text-[#0FA5A0]">Akun demo:</span> demo@quantumpay.id / demo123 (PIN 123456)
          </div>
        </motion.div>
      </div>
    </div>
  );
}
